import { cn } from 'lib/utils'
import { useEffect, useId, useRef } from 'react'
import { barFillProgress, clampBuildStep } from './conversation-chrome'
import { TOTAL_STEPS } from './LogoStepProgress'
import styles from './liquid-logo-reveal.module.css'

export interface LiquidProgressBarProps {
  step: number
  className?: string
  animate?: boolean
}

/** Mobile capsule — ports liquid-bar.reference.html (goo filter + droplet surface). */
export function LiquidProgressBar({ step, className, animate = true }: LiquidProgressBarProps) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const gooId = `lb-goo-${uid}`
  const trackRef = useRef<HTMLDivElement>(null)
  const prevStepRef = useRef(0)
  const surgeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const buildStep = clampBuildStep(step)
  const complete = buildStep === TOTAL_STEPS
  const pctStr = `${(barFillProgress(buildStep) * 100).toFixed(2)}%`
  const motion = animate && !complete
  const hasFill = buildStep > 0

  useEffect(() => {
    const advanced = buildStep > prevStepRef.current
    prevStepRef.current = buildStep
    if (!advanced || !motion || !trackRef.current) return

    const el = trackRef.current
    el.classList.remove(styles.lbarSurging)
    void el.offsetWidth
    el.classList.add(styles.lbarSurging)
    clearTimeout(surgeTimerRef.current)
    surgeTimerRef.current = setTimeout(() => {
      el.classList.remove(styles.lbarSurging)
    }, 950)
    return () => clearTimeout(surgeTimerRef.current)
  }, [buildStep, motion])

  return (
    <div
      ref={trackRef}
      className={cn(styles.lbar, !motion && styles.static, complete && styles.complete, className)}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={TOTAL_STEPS}
      aria-valuenow={buildStep}
      aria-valuetext={complete ? 'Complete' : `Step ${buildStep} of ${TOTAL_STEPS}`}
      aria-label="Interview progress"
    >
      <svg width="0" height="0" aria-hidden className={styles.lbarGooSvg}>
        <defs>
          <filter
            id={gooId}
            x="-200%"
            y="-150%"
            width="500%"
            height="400%"
            colorInterpolationFilters="sRGB"
          >
            <feGaussianBlur in="SourceGraphic" stdDeviation="6.5" result="blur" />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 11 -5"
              result="goo"
            />
            <feGaussianBlur in="goo" stdDeviation="0.8" result="smooth" />
            <feBlend in="SourceGraphic" in2="smooth" />
          </filter>
        </defs>
      </svg>
      <div className={styles.lbarClip}>
        <div className={styles.lbarTrackbg} aria-hidden />
        <div
          className={styles.lbarFill}
          style={{ width: pctStr, filter: `url(#${gooId})` }}
          aria-hidden
        >
          <div className={styles.lbarBody} />
          {motion && hasFill && (
            <div className={styles.lbarSurface}>
              <div className={cn(styles.lbarDrop, styles.lbarDrop1)} />
              <div className={cn(styles.lbarDrop, styles.lbarDrop2)} />
              <div className={cn(styles.lbarDrop, styles.lbarDrop3)} />
              <div className={cn(styles.lbarDrop, styles.lbarDrop4)} />
            </div>
          )}
        </div>
        <div className={styles.lbarSheen} style={{ width: pctStr }} aria-hidden />
      </div>
      <div className={styles.lbarGlow} style={{ width: pctStr }} aria-hidden />
    </div>
  )
}
